/**
 * PerformanceService - A service for monitoring and analyzing application performance
 * 
 * This service provides tools for measuring loading times, network requests,
 * memory usage, and other performance metrics. It's especially useful for
 * tracking performance in offline and low-bandwidth scenarios.
 */
import logger from '../../utils/logger';

export interface PerformanceMetric {
  id: string;
  category: 'network' | 'render' | 'sync' | 'storage' | 'user';
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  status: 'started' | 'completed' | 'failed';
  metadata?: Record<string, any>;
  tags?: string[];
}

export interface PerformanceSnapshot {
  timestamp: number;
  metrics: PerformanceMetric[];
  averages: Record<string, number>;
  memoryUsage?: {
    jsHeapSizeLimit?: number;
    totalJSHeapSize?: number;
    usedJSHeapSize?: number;
  };
  networkInfo?: {
    downlink?: number;
    effectiveType?: string;
    rtt?: number;
    saveData?: boolean;
  };
}

class PerformanceService {
  private static instance: PerformanceService;
  private metrics: Map<string, PerformanceMetric> = new Map();
  private listeners: Array<(metric: PerformanceMetric) => void> = [];
  private isEnabled: boolean = true;
  private sampleRate: number = 1.0; // 100% sampling by default
  private metricsHistory: PerformanceMetric[] = [];
  private historyLimit: number = 1000; // Store last 1000 metrics

  /**
   * Get the singleton instance of PerformanceService
   */
  public static getInstance(): PerformanceService {
    if (!PerformanceService.instance) {
      PerformanceService.instance = new PerformanceService();
    }
    return PerformanceService.instance;
  }

  private constructor() {
    // Initialize performance monitoring
    this.setupPerformanceObserver();
  }

  /**
   * Set up the Performance Observer API to monitor browser performance events
   */
  private setupPerformanceObserver(): void {
    if (typeof window !== 'undefined' && 'PerformanceObserver' in window) {
      try {
        // Monitor navigation and resource timing
        const perfObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            // Generate a performance metric from the entry
            const metric: PerformanceMetric = {
              id: `browser_${entry.entryType}_${Date.now()}`,
              category: 'network',
              name: entry.name,
              startTime: entry.startTime,
              endTime: entry.startTime + (entry as any).duration,
              duration: (entry as any).duration,
              status: 'completed',
              metadata: {
                entryType: entry.entryType,
                raw: entry.toJSON()
              }
            };
            
            this.addMetricToHistory(metric);
            this.notifyListeners(metric);
          });
        });

        // Observe various performance entry types
        perfObserver.observe({ entryTypes: ['navigation', 'resource', 'longtask', 'paint'] });
      } catch (err) {
        logger.warn('Error setting up performance observer:', err);
      }
    }
  }

  /**
   * Start measuring a performance metric
   * 
   * @param category Category of the metric (network, render, etc.)
   * @param name Name of the specific operation being measured
   * @param metadata Additional data to store with the metric
   * @param tags Optional tags for filtering and grouping
   * @returns ID of the created metric for later reference
   */
  public startMeasure(
    category: PerformanceMetric['category'],
    name: string,
    metadata?: Record<string, any>,
    tags?: string[]
  ): string {
    // Apply sampling rate - randomly skip some measurements
    if (!this.isEnabled || Math.random() > this.sampleRate) {
      return '';
    }

    const id = `${category}_${name}_${Date.now()}`;
    const metric: PerformanceMetric = {
      id,
      category,
      name,
      startTime: performance.now(),
      status: 'started',
      metadata,
      tags
    };

    this.metrics.set(id, metric);
    return id;
  }

  /**
   * End a previously started performance measurement
   * 
   * @param id ID of the metric to end (returned from startMeasure)
   * @param status Completion status (completed or failed)
   * @param additionalMetadata Additional data to add to the metric
   * @returns The completed performance metric or null if not found
   */
  public endMeasure(
    id: string,
    status: 'completed' | 'failed' = 'completed',
    additionalMetadata?: Record<string, any>
  ): PerformanceMetric | null {
    if (!id || !this.metrics.has(id)) {
      return null;
    }

    const metric = this.metrics.get(id)!;
    const endTime = performance.now();

    const updatedMetric: PerformanceMetric = {
      ...metric,
      endTime,
      duration: endTime - metric.startTime,
      status,
      metadata: {
        ...metric.metadata,
        ...additionalMetadata
      }
    };

    this.metrics.set(id, updatedMetric);
    this.addMetricToHistory(updatedMetric);
    this.notifyListeners(updatedMetric);

    return updatedMetric;
  }

  /**
   * Measure a complete operation in one call
   * 
   * @param category Category of the metric
   * @param name Name of the operation
   * @param fn Function to measure (can be async)
   * @param metadata Additional data to store
   * @param tags Optional tags for filtering
   * @returns The result of the measured function
   */
  public async measure<T>(
    category: PerformanceMetric['category'],
    name: string,
    fn: () => T | Promise<T>,
    metadata?: Record<string, any>,
    tags?: string[]
  ): Promise<T> {
    const id = this.startMeasure(category, name, metadata, tags);
    
    if (!id) {
      // Measurement was skipped due to sampling or being disabled
      return await fn();
    }

    try {
      const result = await fn();
      this.endMeasure(id, 'completed');
      return result;
    } catch (error) {
      this.endMeasure(id, 'failed', { error: error instanceof Error ? error.message : 'Unknown error' });
      throw error;
    }
  }

  /**
   * Add a listener for performance metric events
   * 
   * @param listener Function to call when new metrics are recorded
   * @returns Function to remove the listener
   */
  public addListener(listener: (metric: PerformanceMetric) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Notify all listeners of a new metric
   */
  private notifyListeners(metric: PerformanceMetric): void {
    this.listeners.forEach(listener => {
      try {
        listener(metric);
      } catch (err) {
        logger.error('Error in performance metric listener:', err);
      }
    });
  }

  /**
   * Add a metric to the history, ensuring we don't exceed the limit
   */
  private addMetricToHistory(metric: PerformanceMetric): void {
    this.metricsHistory.push(metric);
    
    // Trim history if it exceeds the limit
    if (this.metricsHistory.length > this.historyLimit) {
      this.metricsHistory = this.metricsHistory.slice(
        this.metricsHistory.length - this.historyLimit
      );
    }
  }

  /**
   * Get a snapshot of the current performance metrics
   */
  public getSnapshot(): PerformanceSnapshot {
    // Calculate averages for completed metrics
    const completedMetrics = this.metricsHistory.filter(m => m.status === 'completed' && m.duration !== undefined);
    
    // Group by name for averaging
    const metricsByName = completedMetrics.reduce((acc, metric) => {
      const key = `${metric.category}_${metric.name}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(metric.duration!);
      return acc;
    }, {} as Record<string, number[]>);
    
    // Calculate averages
    const averages = Object.entries(metricsByName).reduce((acc, [key, durations]) => {
      const sum = durations.reduce((a, b) => a + b, 0);
      acc[key] = sum / durations.length;
      return acc;
    }, {} as Record<string, number>);
    
    // Get memory info if available
    let memoryUsage = undefined;
    if (performance && (performance as any).memory) {
      memoryUsage = {
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize
      };
    }
    
    // Get network info if available
    let networkInfo = undefined;
    if (navigator && (navigator as any).connection) {
      const conn = (navigator as any).connection;
      networkInfo = {
        downlink: conn.downlink,
        effectiveType: conn.effectiveType,
        rtt: conn.rtt,
        saveData: conn.saveData
      };
    }
    
    return {
      timestamp: Date.now(),
      metrics: [...this.metricsHistory],
      averages,
      memoryUsage,
      networkInfo
    };
  }

  /**
   * Clear all stored metrics
   */
  public clearMetrics(): void {
    this.metrics.clear();
    this.metricsHistory = [];
  }

  /**
   * Enable or disable performance monitoring
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Set the sampling rate for performance metrics (0.0 to 1.0)
   */
  public setSamplingRate(rate: number): void {
    this.sampleRate = Math.max(0, Math.min(1, rate));
  }

  /**
   * Set the history size limit
   */
  public setHistoryLimit(limit: number): void {
    this.historyLimit = Math.max(10, limit);
    
    // Trim history if needed
    if (this.metricsHistory.length > this.historyLimit) {
      this.metricsHistory = this.metricsHistory.slice(
        this.metricsHistory.length - this.historyLimit
      );
    }
  }
}

export const performanceService = PerformanceService.getInstance();
export default performanceService;